// src/CustomEditor.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    ClassicEditor,
    Alignment, Autoformat, AutoImage, AutoLink, Autosave, BalloonToolbar, BlockQuote,
    BlockToolbar, Bold, Code, CodeBlock, CloudServices, Essentials, FindAndReplace,
    FontBackgroundColor, FontColor, FontFamily, FontSize, Heading, Highlight,
    HorizontalLine, ImageBlock, ImageCaption, ImageInline, ImageInsert,
    ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar,
    ImageUpload, Indent, IndentBlock, Italic, Link, LinkImage, List, ListProperties,
    MediaEmbed, Paragraph, PasteFromOffice, RemoveFormat, SourceEditing,
    SpecialCharacters, SpecialCharactersEssentials, Strikethrough, Subscript,
    Superscript, Table, TableCaption, TableCellProperties, TableColumnResize,
    TableProperties, TableToolbar, TextTransformation, TodoList, Underline,
    WordCount, FileRepository,
    // --- Sonradan eklenenler (tumu ucretsiz/GPL) ---
    GeneralHtmlSupport,  // taninmayan HTML'i SILMEK yerine korur
    Fullscreen,          // uzun icerikte tam ekran yazma
    HtmlEmbed,           // hazir gomme kodu (iframe/harita) yapistirma
    Emoji,               // emoji secici
    Mention,             // ⚠️ Emoji'nin ZORUNLU bagimliligi (yoksa editor hic acilmaz)
    ShowBlocks,          // blok yapisini gosterir (Word yapistirmasini duzeltmede)
    UpcastWriter,        // yapistirilan icerige satir ici stil yazmak icin
    ButtonView           // "Yukari/Asagi tasi" dugmeleri icin
} from 'ckeditor5';

import axios from 'axios';
import { UPLOAD_BASE, MEDIA_BASE } from 'helpers/api_config';
import { gorseliKucult } from 'helpers/gorselKucult';
import { repairWordHtml, sinifRenkleri } from './wordImagePaste';
import translations from 'ckeditor5/translations/tr.js';
import 'ckeditor5/ckeditor5.css';
import './EditorStyles.css';

// --- ORTAK YÜKLEME ---
// Hem CKEditor upload adapter'ı hem de Word yapıştırma onarımı bunu kullanır.
export async function uploadBlob(fileOrBlob, filename = 'pasted-image.png') {
    // Haber gorselleri 0,5-1,2 MB PNG olarak gidiyordu; once kucultulur
    // (bkz. helpers/gorselKucult.js). Basarisiz olursa asil dosya gider.
    const yuklenecek = await gorseliKucult(fileOrBlob);
    const body = new FormData();
    body.append('dataFile', yuklenecek, yuklenecek.name || fileOrBlob.name || filename);

    const response = await axios.request({
        method: 'POST',
        url: `${UPLOAD_BASE}/uploadfile`,
        data: body,
        headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (!response.data || !response.data.file || !response.data.file.filename) {
        throw new Error('Yükleme yanıtı beklenen biçimde değil');
    }
    return `${MEDIA_BASE}/${response.data.file.filename}`;
}

// --- UPLOAD ADAPTER ---
function uploadAdapter(loader) {
    return {
      upload: async () => {
        const file = await loader.file;
        try {
            return { default: await uploadBlob(file) };
        } catch (error) {
            // Hata sessizce yutulunca "resim yüklenmiyor" deniyor ama
            // sebebi hiçbir yerde görünmüyordu. CKEditor, reject edilen
            // mesajı kullanıcıya bildirim olarak gösterir.
            const detay = error && error.response
                ? `Sunucu ${error.response.status} döndü`
                : (error && error.message) || 'bilinmeyen hata';
            console.error('Görsel yüklenemedi:', error);
            throw new Error(`Görsel yüklenemedi (${detay}). Lütfen tekrar deneyin.`);
        }
      },
      // Yükleme sırasında iptal edilirse istek boşuna sürmesin
      abort: () => {}
    };
}

function MyCustomUploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
      return uploadAdapter(loader);
    };
}

const LICENSE_KEY = 'GPL';

// --- BLOK TAŞIMA (Yukarı / Aşağı) ---
// CKEditor'de bir görseli başka bir yere almanın tek yolu SÜRÜKLE-BIRAK.
// Telefonda bu çalışmıyor: tarayıcılar dokunmadan sürükleme olayı üretmiyor,
// parmak hareketi sayfayı kaydırıyor. Müşteri "mavi oklarla resmi alta
// gönderemiyorum, yazı yazacağım yere koyamıyorum" dedi (2026-09-15).
// Bu iki düğme seçili bloğu (görsel, paragraf, tablo...) bir sıra yukarı /
// aşağı taşır; tek dokunuşla çalıştığı için dokunmatikte güvenilir.
const OK_YUKARI = '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 3l6 7h-4v7H8v-7H4l6-7z"/></svg>';
const OK_ASAGI = '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 17l-6-7h4V3h4v7h4l-6 7z"/></svg>';

/** Seçimin içinde bulunduğu en üst seviye blok (görsel widget'ı dahil) */
function seciliBlok(editor) {
    const secim = editor.model.document.selection;
    const widget = secim.getSelectedElement();
    if (widget && editor.model.schema.isBlock(widget)) return widget;

    const konum = secim.getFirstPosition();
    let el = konum && konum.parent;
    // Tablo hücresi gibi iç içe yapılarda en dıştaki bloğa çık
    while (el && el.parent && el.parent.rootName === undefined) el = el.parent;
    return el && el.name !== '$root' ? el : null;
}

function BlokTasi(editor) {
    const olustur = (ad, etiket, ikon, yon) => {
        editor.ui.componentFactory.add(ad, locale => {
            const dugme = new ButtonView(locale);
            dugme.set({ label: etiket, icon: ikon, tooltip: true });

            const guncelle = () => {
                const blok = seciliBlok(editor);
                const komsu = blok && (yon < 0 ? blok.previousSibling : blok.nextSibling);
                dugme.isEnabled = !!(blok && komsu);
            };
            editor.model.document.on('change', guncelle);
            guncelle();

            dugme.on('execute', () => {
                const blok = seciliBlok(editor);
                if (!blok) return;
                const komsu = yon < 0 ? blok.previousSibling : blok.nextSibling;
                if (!komsu) return;

                editor.model.change(w => {
                    const hedef = yon < 0 ? w.createPositionBefore(komsu) : w.createPositionAfter(komsu);
                    w.move(w.createRangeOn(blok), hedef);
                    w.setSelection(blok, 'on');
                });
                editor.editing.view.focus();
            });

            return dugme;
        });
    };

    olustur('blokYukariTasi', 'Yukarı taşı', OK_YUKARI, -1);
    olustur('blokAsagiTasi', 'Aşağı taşı', OK_ASAGI, +1);
}

// --- TASLAK (AUTOSAVE) ---
// Autosave eklentisi yukluydu ama `save` verilmedigi icin HICBIR SEY
// yapmiyordu. Yazilan icerik artik tarayicida saklaniyor; sekme kapanir,
// panel yenilenir veya oturum duserse kaybolmuyor.
const DRAFT_PREFIX = 'ckeditorDraft:';
const DRAFT_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000; // 3 gun

const draftKey = id => `${DRAFT_PREFIX}${id || window.location.pathname}`;

export function readDraft(id) {
    try {
        const raw = localStorage.getItem(draftKey(id));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.html) return null;
        if (Date.now() - (parsed.at || 0) > DRAFT_MAX_AGE_MS) {
            localStorage.removeItem(draftKey(id));
            return null;
        }
        return parsed;
    } catch (e) {
        return null;
    }
}

export function clearDraft(id) {
    try { localStorage.removeItem(draftKey(id)); } catch (e) { /* yok say */ }
}

function writeDraft(id, html) {
    try {
        localStorage.setItem(draftKey(id), JSON.stringify({ html, at: Date.now() }));
    } catch (e) {
        // Kota dolduysa taslak atlanir; asil kaydetme etkilenmez
    }
}

/**
 * CKEditor5'in görsel yeniden boyutlandırma tutamaçları yalnızca mouse
 * olaylarını dinler; dokunmatik ekranda köşeden çekme bu yüzden çalışmaz.
 * Aşağıdaki köprü, tutamaç üzerindeki touch olaylarını eşdeğer mouse
 * olaylarına çevirerek mobil/tablette de sürükleyerek boyutlandırmayı açar.
 */
/**
 * DOKUNMATIKTE ARAC CUBUGU ODAGI
 *
 * CKEditor'un butonlari, masaustunde `mousedown` iptal edilerek edit
 * alaninin odagini korur. Telefonda dokunma olayi once geldigi icin bu
 * koruma zaman zaman devreye girmiyor: edit alani odagi dusuyor, secim
 * kayboluyor ve komutlar kapaniyor -> BUTUN DUGMELER SOLUK gorunuyor.
 *
 * Asagidaki koprü, arac cubugundaki bir dugmeye dokunuldugu anda edit
 * alanina odagi geri veriyor.
 */
function attachToolbarFocusBridge(editor) {
    const toolbarElement = editor.ui.view.toolbar && editor.ui.view.toolbar.element;
    if (!toolbarElement) return () => {};

    const onTouchStart = () => {
        try {
            if (!editor.editing.view.document.isFocused) {
                editor.editing.view.focus();
            }
        } catch (e) {
            // Odak verilemezse dugme yine de kendi isini yapmayi dener
        }
    };

    toolbarElement.addEventListener('touchstart', onTouchStart, { passive: true });
    return () => toolbarElement.removeEventListener('touchstart', onTouchStart);
}

function attachTouchResizeBridge(rootElement) {
    if (!rootElement || typeof window === 'undefined') return () => {};

    const HANDLE_SELECTOR = '.ck-widget__resizer__handle';
    let activeHandle = null;

    const dispatchMouse = (type, touch, target) => {
        target.dispatchEvent(new MouseEvent(type, {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: touch.clientX,
            clientY: touch.clientY,
            button: 0,
            buttons: type === 'mouseup' ? 0 : 1
        }));
    };

    const onTouchStart = event => {
        const handle = event.target.closest && event.target.closest(HANDLE_SELECTOR);
        if (!handle) return;

        activeHandle = handle;
        // Sürükleme sırasında sayfanın kaymasını engelle
        event.preventDefault();
        dispatchMouse('mousedown', event.touches[0], handle);
    };

    const onTouchMove = event => {
        if (!activeHandle) return;
        event.preventDefault();
        dispatchMouse('mousemove', event.touches[0], document);
    };

    const onTouchEnd = event => {
        if (!activeHandle) return;
        const touch = event.changedTouches[0];
        dispatchMouse('mouseup', touch, document);
        activeHandle = null;
    };

    rootElement.addEventListener('touchstart', onTouchStart, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);

    return () => {
        rootElement.removeEventListener('touchstart', onTouchStart);
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
        document.removeEventListener('touchcancel', onTouchEnd);
    };
}

export default function CustomEditor({ data, onChange, draftId }) {
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const [stats, setStats] = useState({ words: 0, characters: 0 });
    const [savedAt, setSavedAt] = useState(null);
    const [draft, setDraft] = useState(null);
    const [blocked, setBlocked] = useState(false);
    const detachTouchRef = useRef(null);
    const detachToolbarFocusRef = useRef(null);
    const editorRef = useRef(null);

    // Autosave eklentisinin cagirdigi kaydedici
    const saveDraft = useCallback(html => {
        writeDraft(draftId, html);
        setSavedAt(new Date());
    }, [draftId]);

    // Acilista kurtarilabilir taslak var mi?
    useEffect(() => {
        const found = readDraft(draftId);
        if (found && found.html && found.html !== (data || '')) setDraft(found);
    }, [draftId, data]);

    const restoreDraft = useCallback(() => {
        if (!draft || !editorRef.current) return;
        editorRef.current.setData(draft.html);
        onChange(draft.html);
        setDraft(null);
    }, [draft, onChange]);

    const dismissDraft = useCallback(() => {
        clearDraft(draftId);
        setDraft(null);
    }, [draftId]);

    useEffect(() => {
        setIsLayoutReady(true);
        return () => {
            setIsLayoutReady(false);
            if (detachTouchRef.current) detachTouchRef.current();
            if (detachToolbarFocusRef.current) detachToolbarFocusRef.current();
        };
    }, []);

    const handleReady = useCallback(editor => {
        editorRef.current = editor;

        // --- LISANS/SURUM KONTROLU ---
        // CKEditor, lisans anahtarini kabul etmezse editoru SESSIZCE
        // salt-okunur acar: butun dugmeler soluk gorunur, hicbir sey
        // yazilamaz, ekranda hicbir aciklama cikmaz (sadece konsola bir
        // hata dusuyor). 2026-09-02'de tam olarak bu yasandi: `npm install`
        // ckeditor5'i 47.5.0'dan LTS surumu 47.7.3'e yukseltti ve o
        // surumde 'GPL' anahtari artik engelleniyor. Bir daha ayni sey
        // olursa sebebi ekranda gorunsun.
        // (Acilis aninda kaynak kodu modu kapali oldugu icin bu kontrol
        // yanlis alarm vermez; kaynak kodu modu da salt-okunur yapar.)
        if (editor.isReadOnly) setBlocked(true);

        const root = editor.ui.getEditableElement();
        if (detachTouchRef.current) detachTouchRef.current();
        detachTouchRef.current = attachTouchResizeBridge(root);

        if (detachToolbarFocusRef.current) detachToolbarFocusRef.current();
        detachToolbarFocusRef.current = attachToolbarFocusBridge(editor);

        // --- SINIF İLE VERİLEN RENKLER (Word / Google Dokümanlar) ---
        // Word rengi bir stile bağlıysa `<style>` bloğunda SINIF olarak verir.
        // O blok korunmadığı için renk yapıştırma anında kayboluyordu (müşteri
        // bildirdi, 2026-09-14). Burada sınıf kuralları okunup yapıştırılan
        // içeriğe satır içi yazılıyor.
        //
        // ⚠️ ÖNCELİK 'normal' OLMALI: ClipboardPipeline'ın içeriği ekleyen
        // kendi dinleyicisi 'low' önceliğinde ve DAHA ÖNCE kaydolduğu için
        // 'low' verilirse bizim değişikliğimiz ekleme işleminden SONRA kalıyor
        // ve hiçbir etkisi olmuyor (denendi).
        editor.plugins.get('ClipboardPipeline').on('inputTransformation', (evt, data) => {
            const ham = data.dataTransfer && data.dataTransfer.getData('text/html');
            if (!ham || ham.indexOf('<style') === -1) return;

            const harita = sinifRenkleri(ham);
            if (Object.keys(harita).length === 0) return;

            const yazici = new UpcastWriter(editor.editing.view.document);
            const gez = (ebeveyn) => {
                for (const cocuk of Array.from(ebeveyn.getChildren())) {
                    if (!cocuk.is('element')) continue;
                    for (const sinif of Array.from(cocuk.getClassNames())) {
                        const stil = harita[sinif];
                        if (!stil) continue;
                        Object.entries(stil).forEach(([ad, deger]) => {
                            // Kendi satır içi rengi varsa dokunulmaz, o daha özeldir
                            if (!cocuk.hasStyle(ad)) yazici.setStyle(ad, deger, cocuk);
                        });
                    }
                    gez(cocuk);
                }
            };
            gez(data.content);
        }, { priority: 'normal' });

        // --- WORD'DEN GÖRSEL YAPIŞTIRMA ---
        // CKEditor5'in ücretsiz PasteFromOffice'i Word'ün RTF katmanındaki
        // görselleri çözmez; img'ler file:// yolu ile gelir ve kaybolur.
        // Burada RTF'ten baytları çıkarıp sunucuya yüklüyor, src'yi düzeltiyoruz.
        editor.editing.view.document.on('clipboardInput', (evt, data) => {
            const cd = data.dataTransfer;
            if (!cd) return;

            const html = cd.getData('text/html');
            const rtf = cd.getData('text/rtf');
            if (!html || !rtf || html.indexOf('<img') === -1) return;

            // Onarım asenkron; olayı durdurup iş bitince kendimiz ekliyoruz
            evt.stop();

            repairWordHtml(html, rtf, blob => uploadBlob(blob))
                .then(({ html: fixedHtml, recovered, dropped }) => {
                    const viewFragment = editor.data.processor.toView(fixedHtml);
                    const modelFragment = editor.data.toModel(viewFragment);
                    editor.model.insertContent(modelFragment);

                    if (dropped > 0) {
                        console.warn(
                            `Word yapıştırma: ${recovered} görsel aktarıldı, ` +
                            `${dropped} görsel alınamadı (muhtemelen WMF/EMF vektör biçimi).`
                        );
                    }
                })
                .catch(err => {
                    console.warn('Word yapıştırma onarılamadı, düz metin olarak ekleniyor:', err);
                    const text = cd.getData('text/plain') || '';
                    if (text) {
                        editor.model.insertContent(
                            editor.data.toModel(editor.data.processor.toView(`<p>${text}</p>`))
                        );
                    }
                });
        }, { priority: 'high' });
    }, []);

    const { editorConfig } = useMemo(() => {
        if (!isLayoutReady) return {};

        return {
            editorConfig: {
                toolbar: {
                    // shouldNotGroupWhenFull: false iken CKEditor dar ekranda
                    // butonlarin cogunu "⋮" menusune saklar. Telefonda o menu
                    // acilinca edit alani odagini kaybettigi icin ICINDEKI
                    // BUTUN DUGMELER SOLUK/PASIF gorunuyordu; resim yukleme de
                    // bu yuzden yapilamiyordu. Artik gruplanmiyor, butonlar
                    // alt satira sariyor (EditorStyles.css'teki flex-wrap).
                    items: [
                        'undo', 'redo', '|',
                        'fullscreen', '|',
                        'sourceEditing', 'showBlocks', 'findAndReplace', '|',
                        'heading', '|',
                        'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                        'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', 'code', '|',
                        'alignment', '|',
                        'link', 'insertImage', 'insertTable', 'mediaEmbed', 'htmlEmbed', 'blockQuote', 'codeBlock', 'horizontalLine', 'specialCharacters', 'emoji', '|',
                        'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent', '|',
                        'blokYukariTasi', 'blokAsagiTasi', '|',
                        'highlight', 'removeFormat'
                    ],
                    shouldNotGroupWhenFull: true
                },
                plugins: [
                    Alignment, Autoformat, AutoImage, AutoLink, Autosave, BalloonToolbar, BlockQuote,
                    BlockToolbar, Bold, Code, CodeBlock, CloudServices, Essentials, FindAndReplace,
                    FontBackgroundColor, FontColor, FontFamily, FontSize, Heading, Highlight,
                    HorizontalLine, ImageBlock, ImageCaption, ImageInline, ImageInsert,
                    ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar,
                    ImageUpload, Indent, IndentBlock, Italic, Link, LinkImage, List, ListProperties,
                    MediaEmbed, Paragraph, PasteFromOffice, RemoveFormat, SourceEditing,
                    SpecialCharacters, SpecialCharactersEssentials, Strikethrough, Subscript,
                    Superscript, Table, TableCaption, TableCellProperties, TableColumnResize,
                    TableProperties, TableToolbar, TextTransformation, TodoList, Underline,
                    WordCount, FileRepository,
                    GeneralHtmlSupport, Fullscreen, HtmlEmbed, Emoji, Mention, ShowBlocks,
                    MyCustomUploadAdapterPlugin, BlokTasi
                ],
                heading: {
                    options: [
                        { model: 'paragraph', title: 'Paragraf', class: 'ck-heading_paragraph' },
                        { model: 'heading1', view: 'h1', title: 'Başlık 1', class: 'ck-heading_heading1' },
                        { model: 'heading2', view: 'h2', title: 'Başlık 2', class: 'ck-heading_heading2' },
                        { model: 'heading3', view: 'h3', title: 'Başlık 3', class: 'ck-heading_heading3' },
                        { model: 'heading4', view: 'h4', title: 'Başlık 4', class: 'ck-heading_heading4' }
                    ]
                },
                // Metin seçilince çıkan hızlı biçimlendirme balonu (mobilde çok işe yarıyor)
                balloonToolbar: ['bold', 'italic', 'underline', '|', 'link', '|', 'bulletedList', 'numberedList'],
                // BlockToolbar eklentisi yukluydu ama yapilandirilmamisti, yani
                // hicbir sey yapmiyordu. Blogun solundaki tutamaktan acilan bu
                // menu ozellikle TELEFON/TABLETTE onemli: ust arac cubugunda
                // gruplanip gizlenen komutlara tek dokunusla ulasiliyor.
                blockToolbar: [
                    'heading', '|',
                    'bulletedList', 'numberedList', 'todoList', '|',
                    'insertImage', 'insertTable', 'mediaEmbed', '|',
                    'blockQuote', 'codeBlock', 'horizontalLine', '|',
                    'alignment', 'outdent', 'indent', '|',
                    'blokYukariTasi', 'blokAsagiTasi'
                ],
                fontSize: {
                    options: [10, 12, 14, 'default', 18, 20, 24, 28, 32, 40],
                    supportAllValues: true
                },
                fontFamily: {
                    supportAllValues: true
                },
                image: {
                    toolbar: [
                        'toggleImageCaption', 'imageTextAlternative', '|',
                        // Telefonda görseli sürükleyip taşımak mümkün olmadığı için
                        // taşıma düğmeleri görselin kendi menüsünde de duruyor
                        'blokYukariTasi', 'blokAsagiTasi', '|',
                        'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|',
                        'resizeImage', '|',
                        'linkImage'
                    ],
                    // Dokunmatikte köşeden çekmek zor olabildiği için hazır oranlar
                    resizeOptions: [
                        { name: 'resizeImage:original', label: 'Orijinal', value: null },
                        { name: 'resizeImage:custom', label: 'Özel', value: 'custom' },
                        { name: 'resizeImage:25', label: '%25', value: '25' },
                        { name: 'resizeImage:50', label: '%50', value: '50' },
                        { name: 'resizeImage:75', label: '%75', value: '75' },
                        { name: 'resizeImage:100', label: '%100', value: '100' }
                    ],
                    resizeUnit: '%'
                },
                table: {
                    contentToolbar: [
                        'tableColumn', 'tableRow', 'mergeTableCells',
                        'tableProperties', 'tableCellProperties', 'toggleTableCaption'
                    ]
                },
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://'
                },
                list: {
                    properties: { styles: true, startIndex: true, reversed: true }
                },
                mediaEmbed: {
                    previewsInData: true
                },
                wordCount: {
                    onUpdate: value => setStats({ words: value.words, characters: value.characters })
                },
                // Icerik Word'den ve mobil uygulamadan geliyor; CKEditor
                // tanimadigi etiket/stili normalde SESSIZCE SILER. Asagidaki
                // izin listesi mevcut bicimlendirmenin kaybolmasini onler.
                htmlSupport: {
                    allow: [
                        {
                            name: /^(div|span|section|article|figure|figcaption|iframe|p|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|a|img|strong|em|u|s|blockquote|pre|code|br|hr)$/,
                            attributes: true,
                            classes: true,
                            styles: true
                        }
                    ],
                    // Guvenlik: script/style ve olay ozellikleri asla korunmaz
                    disallow: [
                        { name: /^(script|style|link|meta|base|object|embed|form|input)$/ },
                        { attributes: [/^on./i] }
                    ]
                },
                autosave: {
                    waitingTime: 1500,
                    save: editor => saveDraft(editor.getData())
                },
                placeholder: 'Yazınızı buraya yazın veya yapıştırın…',
                language: 'tr',
                licenseKey: LICENSE_KEY,
                translations: [translations]
            }
        };
    // saveDraft kapanisi da yakalanıyor; bagimliliktan dusurulurse taslak
    // kaydedici bayat kalir. (draftId sabit oldugu surece editor yeniden
    // kurulmaz, yani ek maliyeti yok.)
    }, [isLayoutReady, saveDraft]);

    return (
        <div className="main-container">
            <div className="editor-container editor-container_classic-editor">
                {blocked ? (
                    <div className="editor-blocked-bar">
                        Editör salt-okunur açıldı: CKEditor lisans anahtarını kabul
                        etmedi, bu yüzden bütün düğmeler pasif. Yazılım ekibine
                        bildirin (ckeditor5 sürümü package.json'da 47.5.0'a
                        sabitlenmeli).
                    </div>
                ) : null}
                {draft ? (
                    <div className="editor-draft-bar">
                        <span>
                            Kaydedilmemiş bir taslak bulundu
                            {draft.at
                                ? ` (${new Date(draft.at).toLocaleString('tr-TR')})`
                                : ''}
                            .
                        </span>
                        <span>
                            <button type="button" className="editor-draft-restore" onClick={restoreDraft}>
                                Geri yükle
                            </button>
                            <button type="button" className="editor-draft-dismiss" onClick={dismissDraft}>
                                Yoksay
                            </button>
                        </span>
                    </div>
                ) : null}
                <div className="editor-container__editor">
                    {isLayoutReady && editorConfig && (
                        <CKEditor
                            editor={ClassicEditor}
                            config={editorConfig}
                            data={data}
                            onReady={handleReady}
                            onChange={(event, editor) => {
                                const value = editor.getData();
                                onChange(value);
                            }}
                        />
                    )}
                </div>
                <div className="editor-stats">
                    <span>{stats.words} kelime · {stats.characters} karakter</span>
                    {savedAt ? (
                        <span className="editor-saved">
                            Taslak kaydedildi ·{' '}
                            {savedAt.toLocaleTimeString('tr-TR', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
